import { useEffect, useMemo, useRef, useState, type ClipboardEvent } from 'react'
import { toast } from 'sonner'
import { Button, FileInput, PhotoThumb, Segmented, Skeleton, Textarea, Upload } from '@indipia/ui-core'
import type { CustReqItem, CustReqSrcOption } from '@indipia/shared-features'
import {
  CUST_REQ_DEFAULT_SRC, CUST_REQ_MAX_PHOTOS, CUST_REQ_MESSAGE, mergePhotoFiles, newRequestKey, pickImageFiles, validateContent,
} from '@indipia/shared-features'
import { ErrorBanner } from '../AsyncState'
import { ThumbImg } from '../ThumbImg'

// 고객 요청 입력 폼(등록·수정 공용). 사진은 끌어놓기·붙여넣기·[+ 사진 선택]으로 받고 사진마다 ✕ 로 뺀다.
// 5장 초과분·이미지가 아닌 파일은 안내 후 제외한다(서버도 한 번 더 검증).

export interface CustReqFormValue {
  requestKey: string
  content: string
  srcCode: string
  newFiles: File[]
  /** 수정 모드에서 저장 때 서버에서 지울 기존 사진 */
  removedPhotoIds: number[]
}

interface Props {
  /** 있으면 수정 모드 */
  initial?: CustReqItem
  srcOptions: { status: 'loading' } | { status: 'error'; retry: () => void } | { status: 'ready'; options: CustReqSrcOption[] }
  submitting: boolean
  onSubmit: (v: CustReqFormValue) => void
  onCancel: () => void
}

/** 선택한 새 사진의 실제 썸네일 — 로컬 파일이라 objectURL 로 바로 보인다 */
function LocalThumb({ file, onRemove, disabled }: { file: File; onRemove: () => void; disabled: boolean }) {
  const url = useMemo(() => URL.createObjectURL(file), [file])
  useEffect(() => () => URL.revokeObjectURL(url), [url])
  return <PhotoThumb src={url} alt={file.name} onRemove={onRemove} removeLabel={`${CUST_REQ_MESSAGE.photoRemove}: ${file.name}`} disabled={disabled} />
}

export function CustReqForm({ initial, srcOptions, submitting, onSubmit, onCancel }: Props) {
  const [content, setContent] = useState(initial?.content ?? '')
  const [srcCode, setSrcCode] = useState<string>(() => {
    if (initial) return initial.srcCode
    // 기본값은 카톡 — 선택지에 없으면 값을 지어내지 않고 직접 고르게 한다
    return srcOptions.status === 'ready' && srcOptions.options.some(o => o.value === CUST_REQ_DEFAULT_SRC) ? CUST_REQ_DEFAULT_SRC : ''
  })
  const [files, setFiles] = useState<File[]>([])
  const [removedIds, setRemovedIds] = useState<number[]>([])
  const [touched, setTouched] = useState(false)

  // 등록 시도 키 — 같은 내용으로 다시 보낼 때만 같은 값을 쓴다(내용이 바뀌면 새 키)
  const keyRef = useRef<{ key: string; sig: string } | null>(null)

  const keptPhotos = (initial?.photos ?? []).filter(p => !removedIds.includes(p.photoId))
  const photoTotal = keptPhotos.length + files.length

  const addFiles = (incoming: File[]) => {
    if (incoming.length === 0) return
    const r = mergePhotoFiles(files, incoming, keptPhotos.length)
    if (r.notImage > 0) toast.info(CUST_REQ_MESSAGE.photoNotImage)
    if (r.overflow > 0) toast.info(CUST_REQ_MESSAGE.photoOverflow(r.overflow))
    setFiles(r.next)
  }

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const pasted = pickImageFiles(e.clipboardData?.files)
    if (pasted.length === 0) return // 글자 붙여넣기는 그대로 둔다
    e.preventDefault()
    addFiles(pasted)
  }

  const contentError = touched ? validateContent(content) : null
  const srcError = touched && srcCode === '' ? CUST_REQ_MESSAGE.srcLoadError : null
  const optionsReady = srcOptions.status === 'ready'

  const submit = () => {
    setTouched(true)
    if (validateContent(content) !== null || srcCode === '' || !optionsReady) return
    const sig = `${content.trim()}|${srcCode}|${files.map(f => `${f.name}:${f.size}`).join(',')}`
    if (!keyRef.current || keyRef.current.sig !== sig) keyRef.current = { key: newRequestKey(), sig }
    onSubmit({ requestKey: keyRef.current.key, content: content.trim(), srcCode, newFiles: files, removedPhotoIds: removedIds })
  }

  return (
    <div
      className="p-3 mb-2"
      style={{ border: 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 'var(--radius-md)', background: 'var(--shell-surface)' }}
      onPaste={onPaste}
    >
      <Textarea
        label={CUST_REQ_MESSAGE.contentLabel}
        value={content}
        onChange={e => setContent(e.target.value)}
        rows={4}
        placeholder={CUST_REQ_MESSAGE.contentPlaceholder}
        error={contentError ?? undefined}
        disabled={submitting}
        className="!max-w-none"
        aria-required="true"
      />

      <div className="mt-2" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>{CUST_REQ_MESSAGE.srcLabel}</div>
      <div className="mt-1">
        {srcOptions.status === 'loading' && <Skeleton height={32} />}
        {srcOptions.status === 'error' && <ErrorBanner message={CUST_REQ_MESSAGE.srcLoadError} onRetry={srcOptions.retry} />}
        {srcOptions.status === 'ready' && (
          <Segmented
            size="sm"
            aria-label={CUST_REQ_MESSAGE.srcLabel}
            options={srcOptions.options.map(o => ({ label: o.label, value: o.value }))}
            value={srcCode}
            onChange={v => { if (!submitting) setSrcCode(v) }}
          />
        )}
        {srcError && srcOptions.status === 'ready' && (
          <div className="mt-1" role="alert" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--status-error-700)' }}>{CUST_REQ_MESSAGE.srcLabel}를 골라 주세요</div>
        )}
      </div>

      <div className="mt-2" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>
        {`사진 ${photoTotal}/${CUST_REQ_MAX_PHOTOS}`}
      </div>
      <div className="mt-1">
        <Upload
          accept="image/*"
          multiple
          value={[]}
          onChange={addFiles}
          disabled={submitting || photoTotal >= CUST_REQ_MAX_PHOTOS}
          renderEmpty={() => (
            <div className="dropzone-text">
              <span className="dropzone-title">{CUST_REQ_MESSAGE.photoDrop}</span>
              <span className="dropzone-hint">{CUST_REQ_MESSAGE.photoHint}</span>
            </div>
          )}
        />
      </div>
      <div className="mt-1">
        <FileInput
          accept="image/*"
          multiple
          size="sm"
          disabled={submitting || photoTotal >= CUST_REQ_MAX_PHOTOS}
          onFiles={addFiles}
        >
          {CUST_REQ_MESSAGE.photoSelect}
        </FileInput>
      </div>

      {(keptPhotos.length > 0 || files.length > 0) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {keptPhotos.map(p => (
            <div key={p.photoId} className="relative w-16 h-16">
              <ThumbImg url={p.thumbUrl} className="w-16 h-16" alt="" />
              <Button
                variant="secondary"
                size="sm"
                className="absolute top-0 right-0 !min-w-0 !px-1"
                aria-label={`${CUST_REQ_MESSAGE.photoRemove}: ${p.photoSeq}번`}
                disabled={submitting}
                onClick={() => setRemovedIds(ids => [...ids, p.photoId])}
              >×</Button>
            </div>
          ))}
          {files.map((f, i) => (
            <LocalThumb key={`${f.name}-${f.size}-${i}`} file={f} disabled={submitting} onRemove={() => setFiles(list => list.filter((_, j) => j !== i))} />
          ))}
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" size="sm" disabled={submitting} onClick={onCancel}>{CUST_REQ_MESSAGE.cancel}</Button>
        <Button variant="primary" size="sm" loading={submitting} disabled={!optionsReady} onClick={submit}>
          {submitting ? CUST_REQ_MESSAGE.saving : CUST_REQ_MESSAGE.save}
        </Button>
      </div>
    </div>
  )
}
